import { useState, useEffect, useRef } from 'react';
import { services } from '@/mocks/data';

const cardStyles = [
  { bg: 'bg-background-200', border: 'border-background-300/60', iconBg: 'bg-accent-100', iconColor: 'text-accent-600' },
  { bg: 'bg-background-200', border: 'border-background-300/60', iconBg: 'bg-accent-100', iconColor: 'text-accent-600' },
  { bg: 'bg-background-200', border: 'border-background-300/60', iconBg: 'bg-accent-100', iconColor: 'text-accent-600' },
];

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="relative py-24 md:py-32 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className={`text-center mb-16 md:mb-20 transition-all duration-800 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 rounded-full border border-accent-400 text-sm font-semibold tracking-[0.2em] uppercase text-accent-700 mb-5 hover:border-primary-500 hover:text-primary-500 hover:bg-primary-50 transition-all duration-300 cursor-default">
            Áreas de práctica
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15] mb-4">
            Asesoramiento integral en diversas ramas del derecho
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, idx) => {
            const style = cardStyles[idx % cardStyles.length];
            const isHovered = hoveredId === service.id;

            return (
              <div
                key={service.id}
                onMouseEnter={() => setHoveredId(service.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`group relative rounded-2xl p-8 md:p-10 border transition-all duration-500 ease-out cursor-pointer ${
                  isHovered
                    ? 'bg-primary-500 border-primary-500 shadow-xl scale-[1.02]'
                    : `${style.bg} ${style.border}`
                } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-500 ${
                  isHovered ? 'bg-white/20' : style.iconBg
                }`}>
                  <i className={`${service.icon} text-2xl transition-all duration-500 ${
                    isHovered ? 'text-white' : style.iconColor
                  }`}></i>
                </div>

                <h3 className={`font-heading text-2xl md:text-[1.75rem] lg:text-3xl font-bold mb-5 transition-colors duration-500 ${
                  isHovered ? 'text-white' : 'text-foreground-950'
                }`}>
                  {service.title}
                </h3>

                <ul className={`space-y-2.5 transition-colors duration-500 ${
                  isHovered ? 'text-white/85' : 'text-foreground-600'
                }`}>
                  {service.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-base leading-relaxed">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 transition-colors duration-500 ${
                        isHovered ? 'bg-white' : 'bg-accent-500'
                      }`}></span>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="mt-8" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Wave bottom - transition to About (background-100) */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden"
        style={{ height: '60px', marginBottom: '-1px' }}
      >
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block"
          style={{ display: 'block' }}
        >
          <path d="M0 60H1440V30C1080 12 360 48 0 30V60Z" fill="oklch(0.90 0.012 95)" stroke="none" />
        </svg>
      </div>
    </section>
  );
}